import { UnleashKeys } from '@infield/constants/unleash-keys';
import { AppConfigUseFlagContext } from '@infield/features/app-config/app-config-use-flag.context';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { FC, PropsWithChildren } from 'react';

import type { DeviceType } from './device-context';
import { DeviceContext } from './device-context';
import useKeyboardMode from './hooks/useKeyboardMode';

export enum DeviceTypes {
  mobile = 'MOBILE',
  desktop = 'DESKTOP',
}

export const DEVICE_TYPE_VARIABLE = 'INFIELD_USER_DEVICE';

export const DeviceProvider: FC<PropsWithChildren> = ({ children }) => {
  const [deviceType, setDeviceType] = useState<DeviceType | null>(null);
  const { useFlag } = useContext(AppConfigUseFlagContext);
  const { isEnabled: isNumericalKeyboardsEnabled } = useFlag(
    UnleashKeys.numerical_keyboard,
    {
      forceRerender: true,
      fallback: false,
    }
  );

  const { keyboardMode, setKeyboardModeManually, keyboardConfig } =
    useKeyboardMode(isNumericalKeyboardsEnabled);

  const setDevice = useCallback((deviceType: DeviceType) => {
    localStorage.setItem(DEVICE_TYPE_VARIABLE, deviceType);
    setDeviceType(deviceType);
  }, []);

  const detectDeviceType = () => {
    const isMobileRegex =
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i;
    return isMobileRegex.test(navigator.userAgent)
      ? DeviceTypes.mobile
      : DeviceTypes.desktop;
  };

  useEffect(() => {
    const storedDevice = localStorage.getItem(
      DEVICE_TYPE_VARIABLE
    ) as DeviceType;
    if (storedDevice) {
      setDeviceType(storedDevice);
    } else {
      setDevice(detectDeviceType());
    }
  }, [setDevice]);

  const contextValue = useMemo(
    () => ({
      device: deviceType,
      setDevice,
      keyboardMode,
      setKeyboardModeManually,
      keyboardConfig,
    }),
    [
      deviceType,
      setDevice,
      keyboardMode,
      setKeyboardModeManually,
      keyboardConfig,
    ]
  );

  return (
    <DeviceContext.Provider value={contextValue}>
      {children}
    </DeviceContext.Provider>
  );
};

export const useDeviceContext = () => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDeviceContext must be used within a DeviceProvider');
  }
  return context;
};
